import { Scale } from 'app/common/graph/Scale';
import { AxisOrientation } from './Axis';

export default class GraphUtils {
    private static clipNumber = 0;

    /**
     * SVG clip IDs must be unique across the WHOLE document, otherwise they will not function.
     * (they silently fail to work).
     */
    static getUniqueClipId() {
        return 'UniqueClipId_' + GraphUtils.clipNumber++;
    }

    /**
     * A rough heuristic for choosing the best tick interval, given the constraints of
     *  * graphical space - as defined by scale.source
     *  * number of ticks required
     *  * approximate label size
     *
     *  Recommended intervals may be provided as input
     *
     *  Note : this heuristic can be externalised from the Axis component, since there are other components (e.g. Grid lines)
     *  that need to share the same tick interval.
     * @param scale
     * @param numTicks
     * @param possibleTickIntervals
     * @param labelSize
     */
    static chooseBestTickInterval(scale: Scale, numTicks: number, possibleTickIntervals: number[], labelSize: number) {
        const visibleRange = scale.target;
        let minTargetWidthPerLabel = Math.abs(visibleRange.to - visibleRange.from) / (numTicks - 1);
        let bestInterval = GraphUtils.getBestIntervalFor(possibleTickIntervals, minTargetWidthPerLabel);

        labelSize += 10; // at least 10 pixels between labels
        const numPossibleLabels = Math.floor(Math.abs(scale.source.to - scale.source.from) / labelSize);

        if (numPossibleLabels > 1 && numPossibleLabels < numTicks) {
            minTargetWidthPerLabel = Math.abs(visibleRange.to - visibleRange.from) / (numPossibleLabels - 1);
            bestInterval = GraphUtils.getBestIntervalFor(possibleTickIntervals, minTargetWidthPerLabel);
        }
        return bestInterval;
    }

    /**
     * Heuristic to guess an approximate label size
     * (note : the size is in the orientation of the axis).
     * @param orientation
     * @param exampleLabel
     */
    static getApproximateLabelSize(orientation: AxisOrientation, exampleLabel: string) {
        switch (orientation) {
            case AxisOrientation.TOP:
            case AxisOrientation.BOTTOM:
                return GraphUtils.getTextWidth(exampleLabel);
            case AxisOrientation.RIGHT:
            case AxisOrientation.LEFT:
                return GraphUtils.getTextWidth('A') * 1.2;
        }
    }

    static getBestIntervalFor(possibleTickIntervals: number[], minInterval: number) {
        if (possibleTickIntervals) {
            let bestInterval = Math.max(...possibleTickIntervals);
            for (const interval of possibleTickIntervals) {
                if (interval >= minInterval && interval < bestInterval) {
                    bestInterval = interval;
                }
            }
            return bestInterval;
        } else {
            return Math.ceil(minInterval / 10) * 10; // try to at least round to nearest unit of 10.
        }
    }

    static getTextWidth(text, font?) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        context.font = font || getComputedStyle(document.body).font;
        return context.measureText(text).width;
    }
}
